import { useEffect } from "react";

/**
 * A picture that fails to load never shows the browser's broken-image icon
 * or its alt text spilled across the layout: the image is hidden and its
 * container wears a soft tint, so a missing logo, thumbnail or cover reads
 * as a quiet placeholder. Pictures that carry their own designed fallback
 * (`onError` swaps them out) are unaffected, as they are no longer an <img>.
 * One capturing listener, since `error` does not bubble; images that failed
 * before hydration are swept once.
 */
function soften(img: HTMLImageElement) {
  if (img.dataset.imgFailed !== undefined) return;

  img.dataset.imgFailed = "";
  img.style.visibility = "hidden";
  const holder = img.parentElement;

  if (
    holder &&
    getComputedStyle(holder).backgroundColor === "rgba(0, 0, 0, 0)" &&
    getComputedStyle(holder).backgroundImage === "none"
  ) {
    holder.style.backgroundColor =
      "color-mix(in srgb, currentColor 7%, transparent)";
  }
}

export function BrokenImageGuard() {
  useEffect(() => {
    const onError = (event: Event) => {
      if (event.target instanceof HTMLImageElement) soften(event.target);
    };
    document.addEventListener("error", onError, true);

    for (const img of document.images) {
      if (img.complete && img.naturalWidth === 0 && img.currentSrc) soften(img);
    }

    return () => document.removeEventListener("error", onError, true);
  }, []);
  return null;
}
